import React from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import "./PriorArt.css";

const PriorArtReport: React.FC = () => {
  return (
    <div className="prior-art-page">
      {/* Hero Section with Wave Design */}
      <div className="hero-section py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="hero-content">
                <span className="badge bg-primary-soft mb-3 px-3 py-2">Prior Art Search</span>
                <h1 className="display-4 fw-bold mb-4">
                  Discover What Exists,<br />
                  Innovate What's Next
                </h1>
                <p className="lead mb-5">
                  Our AI-powered prior art search goes beyond traditional methods, uncovering hidden 
                  connections and relevant technologies across global patent databases and scientific 
                  literature.
                </p>
                <div className="d-flex gap-3">
                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="px-4 fw-semibold"
                  >
                    Start Your Search
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    size="lg" 
                    className="px-4"
                  >
                    Watch Demo
                  </Button>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-visual">
                <div className="search-visual">
                  <div className="search-circles">
                    {[1, 2, 3].map((_, index) => (
                      <div key={index} className={`search-circle circle-${index + 1}`} />
                    ))}
                  </div>
                  <div className="search-lines">
                    {[1, 2, 3, 4].map((_, index) => (
                      <div key={index} className={`search-line line-${index + 1}`} />
                    ))}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Key Areas Section */}
      <div className="key-areas-section py-5">
        <Container>
          <h2 className="text-center mb-4">Key Areas of Prior Art Search</h2>
          <Row className="g-4">
            <Col md={6} lg={3}>
              <Card className="h-100 border-0">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-journal-text text-primary fs-3 me-3"></i>
                    <h4 className="mb-0">Academic Research</h4>
                  </div>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">• Research Papers</li>
                    <li className="mb-2">• Journal Articles</li>
                    <li className="mb-2">• Conference Proceedings</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="h-100 border-0">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-file-earmark-text text-primary fs-3 me-3"></i>
                    <h4 className="mb-0">Patent Literature</h4>
                  </div>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">• Granted Patents</li>
                    <li className="mb-2">• Patent Applications</li>
                    <li className="mb-2">• Technical Documents</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="h-100 border-0">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-globe text-primary fs-3 me-3"></i>
                    <h4 className="mb-0">Public Domain</h4>
                  </div>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">• Technical Standards</li>
                    <li className="mb-2">• Industry Guidelines</li>
                    <li className="mb-2">• Public Disclosures</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="h-100 border-0">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-book text-primary fs-3 me-3"></i>
                    <h4 className="mb-0">Non-Patent Lit</h4>
                  </div>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">• Trade Publications</li>
                    <li className="mb-2">• Technical Manuals</li>
                    <li className="mb-2">• Product Documentation</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Process Section */}
      <div className="process-section py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-3">How It Works</h2>
            <p className="lead text-muted">
              Our streamlined process delivers comprehensive results in four simple steps
            </p>
          </div>
          <Row className="g-4">
            {[
              {
                step: 1,
                title: "Submit Details",
                description: "Share your invention's technical specifications and scope"
              },
              {
                step: 2,
                title: "AI Analysis",
                description: "Our AI scans millions of documents across global databases"
              },
              {
                step: 3,
                title: "Expert Review",
                description: "Patent experts validate and refine the AI findings"
              },
              {
                step: 4,
                title: "Detailed Report",
                description: "Receive comprehensive analysis with actionable insights"
              }
            ].map((step, index) => (
              <Col key={index} md={3}>
                <Card className="process-card h-100 border-0">
                  <Card.Body className="p-4 text-center">
                    <div className="step-number mb-3">{step.step}</div>
                    <h4 className="fw-bold mb-2">{step.title}</h4>
                    <p className="text-muted mb-0">{step.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* Features Grid */}
      <div className="features-section py-5">
        <Container>
          <Row className="g-4">
            {[
              {
                icon: "bi-search",
                title: "Semantic Search",
                description: "AI-powered search understands concepts, not just keywords"
              },
              {
                icon: "bi-globe",
                title: "Global Coverage",
                description: "Access patents and literature from 150+ countries"
              },
              {
                icon: "bi-lightning",
                title: "Real-time Updates",
                description: "Stay current with the latest patent publications"
              },
              {
                icon: "bi-graph-up",
                title: "Analytics Dashboard",
                description: "Visual insights into technology trends and gaps"
              },
              {
                icon: "bi-shield-check",
                title: "Quality Assurance",
                description: "Multi-level validation process ensures accuracy"
              },
              {
                icon: "bi-file-earmark-text",
                title: "Custom Reports",
                description: "Tailored reports matching your specific needs"
              }
            ].map((feature, index) => (
              <Col key={index} md={4}>
                <Card className="feature-card h-100 border-0">
                  <Card.Body className="p-4">
                    <i className={`bi ${feature.icon} fs-2 text-primary mb-3`}></i>
                    <h4 className="fw-bold mb-2">{feature.title}</h4>
                    <p className="text-muted mb-0">{feature.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* CTA Section */}
      {/* <div className="cta-section py-5">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <h2 className="display-6 fw-bold mb-4">
                Ready to Start Your Prior Art Search?
              </h2>
              <p className="lead text-muted mb-5">
                Join innovative companies using Fyled AI to accelerate their patent research
              </p>
              <div className="d-flex gap-3 justify-content-center">
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="px-4 fw-semibold"
                >
                  Get Started
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="lg" 
                  className="px-4"
                >
                  Schedule Demo
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div> */}
    </div>
  );
};

export default PriorArtReport; 